// prettier-ignore-start
import FullCalendar from '@fullcalendar/react';
// prettier-ignore-end
import { CalendarOptions, EventInput } from '@fullcalendar/common';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Typography } from '@mui/material';
import { parse, sub } from 'date-fns';
import { get, groupBy, map } from 'lodash';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { getTranslatedValue } from 'src/common/utils/format-utils';
import { dateFormat } from '../../../hooks';
import useStyles from './styles';

interface SchedulesCalendarProps {
  config?: CalendarOptions;
  events?: EventInput[];
  addable?: boolean;
}

export const SchedulesCalendar: ComponentType<SchedulesCalendarProps> = ({
  events = [],
  config: scheduleConfig,
  addable = false,
}) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  const mappedEvents = map(events, (x: any) => ({
    ...x,
    title: getTranslatedValue(x.title, i18n.language),
    start: dateFormat(new Date(x.start), 'Y-M-D'),
  }));
  const groupedEvents = groupBy(mappedEvents, 'start');

  const handleDateSet = (params: Record<string, any>) => console.log('dateSet', params);

  const handleDayClick = (params: Record<string, any>) => console.log('dayClick', params);

  const handleEventClick = (params: Record<string, any>) => console.log('eventClick', params);

  const handleCellClassNames = ({ date }: Record<string, any>) => {
    const cellClasses: string[] = [];
    const dateString: any = dateFormat(date, 'Y-M-D');
    const now = new Date();
    const currentSelectedDate = parse(dateString, 'yyyy-MM-dd', new Date());

    if (currentSelectedDate < sub(now, { days: 1 })) {
      cellClasses.push(classes.disabled);
    }

    if (get(groupedEvents, dateString)) cellClasses.push(classes.hasEventsCell);
    else cellClasses.push(classes.emptyEventsCell);

    if (addable) cellClasses.push(classes.addEventsCell);

    return cellClasses.join(' ');
  };

  const handleEventContent = ({ event }: any) => {
    const { title, start, end, backgroundColor } = event;
    return (
      <div className={classes.card} style={{ backgroundColor }}>
        <Typography variant="h6" component="header" mb={0.5}>
          {dateFormat(new Date(start), 'T')}
          {end && <> - {dateFormat(new Date(end), 'T')}</>}
        </Typography>
        <Typography
          variant="caption"
          component="div"
          sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
        >
          {title}
        </Typography>
      </div>
    );
  };

  const config: CalendarOptions = {
    events: mappedEvents,
    initialView: 'dayGridMonth',
    dayHeaders: true,
    aspectRatio: 1,
    expandRows: true,
    locale: i18n.language,
    weekends: true,
    firstDay: 1,
    selectable: true,
    dayMaxEventRows: 3,
    lazyFetching: true,
    headerToolbar: {
      left: 'prev',
      center: 'title',
      right: 'next',
    },
    dayCellClassNames: handleCellClassNames,
    moreLinkText: (num: number) => t('more.count', `еще ${num}`, { count: num }),
    datesSet: handleDateSet,
    dateClick: handleDayClick,
    eventContent: handleEventContent,
    eventClick: handleEventClick,
    ...scheduleConfig,
  };

  return (
    <div className={classes.root}>
      <FullCalendar plugins={[dayGridPlugin, interactionPlugin]} {...config} />
    </div>
  );
};
